import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, CalendarDays, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../api/axios';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=verify identity, 2=new password, 3=success
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [userName, setUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-identity', { email, birthday });
      setResetToken(response.data.reset_token);
      setUserName(response.data.name || '');
      toast.success('Identity verified!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email,
        reset_token: resetToken,
        new_password: newPassword,
      });
      toast.success('Password reset successful!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="mb-8 flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_employee-timetrack-7/artifacts/86ah3aef_SNR%20LOGO.png"
                alt="S.N. Randar & Associates"
                className="w-80 h-80 object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold font-heading tracking-tight mb-4 text-primary-foreground">
              S.N. Randar & Associates
            </h1>
            <p className="text-xl text-primary-foreground/90">
              Chartered Accountants - Project Financing
            </p>
          </motion.div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          {step === 1 && (
            <>
              <div className="mb-8">
                <Link to="/login" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-login-link">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Login
                </Link>
                <h2 className="text-3xl font-bold font-heading tracking-tight mb-2">
                  Reset Password
                </h2>
                <p className="text-muted-foreground">
                  Verify your identity using your email and date of birth
                </p>
              </div>

              <form onSubmit={handleVerifyIdentity} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      data-testid="forgot-email-input"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="none"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthday">Date of Birth</Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="birthday"
                      data-testid="forgot-birthday-input"
                      type="date"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter the date of birth you used during registration
                  </p>
                </div>

                <Button
                  type="submit"
                  data-testid="verify-identity-button"
                  className="w-full"
                  disabled={loading || !email || !birthday}
                >
                  {loading ? 'Verifying...' : 'Verify Identity'}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-8">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
                <h2 className="text-3xl font-bold font-heading tracking-tight mb-2">
                  Set New Password
                </h2>
                <p className="text-muted-foreground">
                  {userName ? `Hi ${userName.split(' ')[0]}, create` : 'Create'} a new password for your account
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      data-testid="new-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      data-testid="confirm-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-destructive">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  data-testid="reset-password-button"
                  className="w-full"
                  disabled={loading || !newPassword || newPassword !== confirmPassword}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="mb-6"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
              </motion.div>
              <h2 className="text-3xl font-bold font-heading tracking-tight mb-2">
                Password Reset!
              </h2>
              <p className="text-muted-foreground mb-8">
                Your password has been reset successfully. You can now login with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
